import { useState, useRef } from "react"
import { BASE_URL } from "../../apiConfig";
import { useAuth } from "../../context/AuthProvider";
import { toast } from 'sonner';
import { Link } from "react-router-dom";

export default function CreateCategoryForm() {
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    image: null, 
  })
  const [imagePreview, setImagePreview] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [success, setSuccess] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef(null)
  const { token } = useAuth();

  // Generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim("-")
  }

  const handleTitleChange = (e) => {
    const title = e.target.value
    setFormData({
      ...formData,
      title,
      slug: generateSlug(title),
    })
    // Clear the specific error for title when the user types
    if (errors.title) {
      setErrors((prevErrors) => ({ ...prevErrors, title: "" }))
    }
  }

  const handleSlugChange = (e) => {
    const slug = generateSlug(e.target.value)
    setFormData({ ...formData, slug })
    if (errors.slug) {
      setErrors((prevErrors) => ({ ...prevErrors, slug: "" }))
    }
  }

  const handleImageUpload = (file) => {
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreview(e.target.result)
        setFormData({ ...formData, image: file })
      }
      reader.readAsDataURL(file)
      if (errors.image) {
        setErrors((prevErrors) => ({ ...prevErrors, image: "" }))
      }
    } else {
      setImagePreview("");
      setFormData({ ...formData, image: null });
      setErrors((prevErrors) => ({ ...prevErrors, image: "The uploaded file must be an image." }));
    }
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageUpload(e.dataTransfer.files[0])
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.title.trim()) {
      newErrors.title = "The title is required."
    } else if (formData.title.length < 3) {
      newErrors.title = "The title must be at least 3 characters."
    }

    if (!formData.slug.trim()) {
      newErrors.slug = "The slug is required."
    } else if (formData.slug.length < 3) {
      newErrors.slug = "The slug must be at least 3 characters."
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error("Please correct the errors in the form.");
      return;
    }

    setIsLoading(true)
    setErrors({});
    setSuccess(false);

    const dataToSend = new FormData();
    dataToSend.append('title', formData.title);
    dataToSend.append('slug', formData.slug);
    if (formData.image) {
      dataToSend.append('image', formData.image);
    }

    try {
      const response = await fetch(`${BASE_URL}/api/categories`, {
        method: 'POST',
        headers: {
         'Authorization': `Bearer ${token}` },
        body: dataToSend,
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(true);
        
        toast.success(result.message || "Category created successfully!");
        setTimeout(() => {
          setFormData({ title: "", slug: "", image: null })
          setImagePreview("")
          setSuccess(false)
        }, 3000)
      } else if (response.status === 422) {
        setErrors(result.errors);
        toast.error(result.message || "Validation failed. Please check the form.");
      } else {
        toast.error(result.message || "An error occurred.");
      }
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error("Network error or an unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  }

  const removeImage = () => {
    setImagePreview("")
    setFormData({ ...formData, image: null }) // Set image to null
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
    // Clear image error if any
    if (errors.image) {
      setErrors((prevErrors) => ({ ...prevErrors, image: "" }))
    }
  }

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
      {/* The success message can stay, but the toast will also provide feedback */}
      {success && (
        <div className="bg-[var(--color-primary-500)] text-white p-6 text-center">
          <div className="flex items-center justify-center mb-2">
            <svg className="h-8 w-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-lg font-semibold">Category Created Successfully!</span>
          </div>
          <p className="text-[var(--color-primary-50)]">Your new category has been added to the system.</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-8">
        {/* Title Field */}
        <div className="mb-8">
          <label htmlFor="title" className="block font-semibold text-sm text-gray-900 mb-3">
            Category Title *
          </label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={handleTitleChange}
            placeholder="Enter category title..."
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              errors.title
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
          />
          {errors.title && <p className="mt-2 text-red-600 text-xs">{errors.title}</p>}
        </div>

        {/* Slug Field */}
        <div className="mb-8">
          <label htmlFor="slug" className="block font-semibold text-sm text-gray-900 mb-3">
            URL Slug *
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
              /categories/
            </span>
            <input
              type="text"
              id="slug"
              value={formData.slug}
              onChange={handleSlugChange}
              placeholder="category-slug"
              className={`w-full pl-32 py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                errors.slug
                  ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                  : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
              }`}
            />
          </div>
          {errors.slug && <p className="mt-2 text-red-600 text-xs">{errors.slug}</p>}
          <p className="mt-2 text-gray-500 text-xs">
            The slug is automatically generated from the title, but you can customize it.
          </p>
        </div>

        {/* Image Upload */}
        <div className="mb-8">
          <label className="block font-semibold text-gray-900 mb-3 text-sm">Category Image</label>

          {!imagePreview ? (
            <div
              className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200 ${
                dragActive ? "border-[var(--color-primary-500)] bg-[var(--color-primary-50)]" : "border-gray-300 hover:border-[var(--color-primary-500)] hover:bg-gray-50"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0])}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />

              <div className="space-y-4">
                <div className="w-14 h-14 mx-auto bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                  <svg className="h-8 w-8 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Drop your image here</p>
                  <p className="text-gray-500 text-sm">or click to browse files</p>
                </div>
                <p className="text-xs text-gray-400">PNG, JPG, GIF up to 2MB</p> {/* Updated max size */}
              </div>
            </div>
          ) : (
            <div className="relative rounded-2xl overflow-hidden border-2 border-gray-200">
              <div className="aspect-video relative bg-gray-100">
                <img src={imagePreview || "/placeholder.svg"} alt="Category preview" fill className="object-cover" />
              </div>
              <div className="absolute top-4 right-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-white/90 backdrop-blur-sm text-gray-700 p-2 rounded-full shadow-lg hover:bg-white transition-all duration-200"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={removeImage}
                  className="bg-red-500/90 backdrop-blur-sm text-white p-2 rounded-full shadow-lg hover:bg-red-500 transition-all duration-200"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0])}
                className="hidden"
              />
            </div>
          )}
          {errors.image && <p className="mt-2 text-red-600 text-sm">{errors.image}</p>}
        </div>

        {/* Form Actions */}
        <div className="flex sm:flex-row gap-4 pt-6 border-t border-gray-200">
          <Link to="/categories" className="block cursor-pointer text-center flex-1 py-3 px-4 cursor-pointer text-sm border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">
            <button
              type="button"
              disabled={isLoading}
              className='cursor-pointer'
            >
              Annuler  
            </button>
          </Link>  
          <button
            type="submit"
            disabled={isLoading}
            className={`flex-1 py-3 cursor-pointer px-4 text-sm font-semibold rounded-2xl transition-all duration-200 ${
              isLoading
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white shadow-lg hover:shadow-xl transform hover:scale-105"
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Enregistrement...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                Enregister
                <svg className="h-4 w-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </span>
            )}
          </button>
        </div>
      </form>

      {/* Preview Section */}
      {(formData.title || imagePreview) && (
        <div className="border-t border-gray-200 p-8 bg-gray-50 ">
          <h3 className=" font-semibold text-gray-900 mb-4">Preview</h3>
          <div className="bg-white rounded-2xl shadow-md w-80 overflow-hidden border border-gray-100 max-w-sm">
            <div className="aspect-video relative bg-gradient-to-br from-[var(--color-primary-50)] via-white to-gray-100">
              {imagePreview ? (
                <img src={imagePreview || "/placeholder.svg"}   alt="Preview" fill className="object-cover" />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <svg className="h-10 w-10 text-[var(--color-primary-100)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                </div>
              )}
              <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm text-[var(--color-primary-500)] px-2 py-1 rounded-full text-[10px] font-semibold">
                0 items
              </div>
            </div>
            <div className="p-4">
              <h4 className="font-bold text-gray-900 mb-2 text-sm">{formData.title || "Category Title"}</h4>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}